import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useNavigate, useLocation } from 'react-router-dom';

const CLOUD_FUNCTIONS_BASE = 'https://us-central1-skilltrade-app-2026.cloudfunctions.net';

function JobRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [showProfile, setShowProfile] = useState(false);
  const [viewedProfile, setViewedProfile] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [reviewingRequest, setReviewingRequest] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [submittedReviews, setSubmittedReviews] = useState({});
  const [jobConfirmations, setJobConfirmations] = useState({});
  const [user, setUser] = useState(null);

  const [stripeOnboardingDone, setStripeOnboardingDone] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [pendingAcceptId, setPendingAcceptId] = useState(null);
  const [stripeRedirecting, setStripeRedirecting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Fix for mobile — wait for auth to initialize
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (!u) {
        setLoading(false);
        setStripeLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Check if returning from Stripe onboarding
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('stripe') === 'success') {
      checkStripeStatus();
      window.history.replaceState({}, '', '/job-requests');
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load worker's Stripe status
  useEffect(() => {
    if (!user) return;
    const loadStripeStatus = async () => {
      try {
        const workerDoc = await getDoc(doc(db, 'workerProfiles', user.uid));
        const workerData = workerDoc.data() || {};
        if (workerData.stripeOnboardingDone && workerData.stripePayoutsEnabled) {
          setStripeOnboardingDone(true);
        } else if (workerData.stripeAccountId) {
          await checkStripeStatus();
        }
      } catch (err) { console.error(err); }
      setStripeLoading(false);
    };
    loadStripeStatus();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkStripeStatus = async () => {
    if (!user) return;
    try {
      const res = await fetch(`${CLOUD_FUNCTIONS_BASE}/checkWorkerStripeStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workerId: user.uid }),
      });
      const data = await res.json();
      if (data.onboardingDone && data.payoutsEnabled) {
        setStripeOnboardingDone(true);
        if (pendingAcceptId) {
          await doAccept(pendingAcceptId);
          setPendingAcceptId(null);
          setShowStripeModal(false);
        }
      }
    } catch (err) { console.error(err); }
  };

  const handleStartStripeOnboarding = async () => {
    if (!user) return;
    setStripeRedirecting(true);
    try {
      const workerDoc = await getDoc(doc(db, 'workerProfiles', user.uid));
      const workerData = workerDoc.data() || {};
      const res = await fetch(`${CLOUD_FUNCTIONS_BASE}/createWorkerStripeAccount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workerId: user.uid,
          workerEmail: user.email,
          workerName: workerData.name || user.email,
          returnUrl: `${window.location.origin}/job-requests?stripe=success`,
          refreshUrl: `${window.location.origin}/job-requests?stripe=refresh`,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Error starting Stripe setup. Please try again.');
        setStripeRedirecting(false);
      }
    } catch (err) {
      console.error(err);
      alert('Error connecting to payment system. Please try again.');
      setStripeRedirecting(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    const fetchRequests = async () => {
      try {
        const q = query(collection(db, 'jobRequests'), where('workerId', '==', user.uid));
        const querySnapshot = await getDocs(q);
        const data = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
        setRequests(data);

        const reviewed = {};
        const confsMap = {};
        for (const job of data) {
          const reviewId = `${job.id}_${user.uid}`;
          const reviewDoc = await getDoc(doc(db, 'reviews', reviewId));
          if (reviewDoc.exists()) reviewed[job.id] = true;
          if (job.status === 'completed' || isJobComplete(job)) {
            const confsSnap = await getDocs(
              query(collection(db, 'jobConfirmations'), where('jobRequestId', '==', job.id))
            );
            confsMap[job.id] = confsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          }
        }
        setSubmittedReviews(reviewed);
        setJobConfirmations(confsMap);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchRequests();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const isJobComplete = (request) => {
    if (request.status === 'completed') return true;
    if (!request.jobEndDate || request.status !== 'accepted') return false;
    return new Date() > new Date(request.jobEndDate + 'T23:59:59');
  };

  const updateResponseTime = async (requestId) => {
    try {
      const jobDoc = await getDoc(doc(db, 'jobRequests', requestId));
      const jobData = jobDoc.data();
      if (!jobData?.createdAt) return;
      const minutesDiff = Math.round((new Date() - jobData.createdAt.toDate()) / 60000);
      const workerDocRef = doc(db, 'workerProfiles', user.uid);
      const workerDoc = await getDoc(workerDocRef);
      const workerData = workerDoc.data() || {};
      const totalResponseMinutes = (workerData.totalResponseMinutes || 0) + minutesDiff;
      const responseCount = (workerData.responseCount || 0) + 1;
      await updateDoc(workerDocRef, {
        totalResponseMinutes,
        responseCount,
        avgResponseMinutes: Math.round(totalResponseMinutes / responseCount),
      });
    } catch (err) { console.error(err); }
  };

  const isWithin48Hours = (jobDate, jobTime) => {
    if (!jobDate || !jobTime) return false;
    const diff = new Date(`${jobDate}T${jobTime}`) - new Date();
    return diff > 0 && diff <= 48 * 60 * 60 * 1000;
  };

  const doAccept = async (requestId) => {
    try {
      const res = await fetch(`${CLOUD_FUNCTIONS_BASE}/chargeContractorForJob`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobRequestId: requestId, workerId: user.uid }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        if (data.code === 'no_payment_method') {
          alert("This contractor doesn't have a valid card on file yet, so the job can't be confirmed. Try messaging them to set up payment.");
        } else if (data.code === 'already_charged' || data.code === 'not_pending') {
          setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'accepted' } : r));
        } else if (data.code === 'charge_failed' || data.code === 'charge_incomplete') {
          alert("The contractor's card was declined, so the job couldn't be confirmed. They've been notified to update their payment method.");
        } else {
          alert(data.error || 'Could not confirm the job. Please try again.');
        }
        return;
      }

      await updateResponseTime(requestId);
      setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'accepted' } : r));
    } catch (err) {
      console.error(err);
      alert('Could not reach the payment system. Please try again.');
    }
  };

  const handleAccept = async (requestId) => {
    if (!stripeOnboardingDone) {
      setPendingAcceptId(requestId);
      setShowStripeModal(true);
      return;
    }
    await doAccept(requestId);
  };

  const handleDecline = async (requestId) => {
    try {
      await updateDoc(doc(db, 'jobRequests', requestId), {
        status: 'declined', respondedAt: new Date(),
      });
      await updateResponseTime(requestId);
      setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'declined' } : r));
    } catch (err) { console.error(err); }
  };

  const handleCancel = async (request) => {
    const late = isWithin48Hours(request.jobDate, request.jobTime);
    const message = late
      ? `⚠️ Late Cancellation Warning\n\nThis job starts within 48 hours. Cancelling now will be recorded as a LATE CANCELLATION on your profile.\n\nAre you sure you want to cancel?`
      : `Cancel this accepted job? This is a free cancellation since it's more than 48 hours before the job starts.`;
    if (!window.confirm(message)) return;
    try {
      const workerDoc = await getDoc(doc(db, 'workerProfiles', user.uid));
      const currentStats = workerDoc.data() || {};
      await updateDoc(doc(db, 'jobRequests', request.id), {
        status: 'cancelled', cancelledAt: new Date(),
        cancelledBy: 'worker', isLateCancellation: late,
      });
      await updateDoc(doc(db, 'workerProfiles', user.uid), {
        lateCancellations: (currentStats.lateCancellations || 0) + (late ? 1 : 0),
        totalCancellations: (currentStats.totalCancellations || 0) + 1,
      });
      setRequests(prev => prev.map(r =>
        r.id === request.id ? { ...r, status: 'cancelled', isLateCancellation: late } : r
      ));
    } catch (err) { console.error(err); }
  };

  const handleViewContractorProfile = async (request) => {
    try {
      const docSnap = await getDoc(doc(db, 'contractorProfiles', request.contractorId));
      if (docSnap.exists()) {
        setViewedProfile({ type: 'contractor', ...docSnap.data() });
      } else {
        setViewedProfile({ type: 'contractor', email: request.contractorEmail, companyName: request.contractorEmail });
      }
      setShowProfile(true);
    } catch (err) { console.error(err); }
  };

  const handleOpenReview = (request) => {
    setReviewingRequest(request);
    setReviewRating(0);
    setReviewComment('');
    setShowReview(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewRating || !reviewingRequest) return;
    setSubmittingReview(true);
    try {
      const reviewId = `${reviewingRequest.id}_${user.uid}`;
      await setDoc(doc(db, 'reviews', reviewId), {
        jobRequestId: reviewingRequest.id,
        reviewerId: user.uid,
        reviewerType: 'worker',
        revieweeId: reviewingRequest.contractorId,
        revieweeType: 'contractor',
        revieweeName: reviewingRequest.contractorEmail,
        rating: reviewRating,
        comment: reviewComment.trim(),
        jobDescription: reviewingRequest.jobDescription,
        jobDate: reviewingRequest.jobDate,
        jobEndDate: reviewingRequest.jobEndDate,
        createdAt: new Date(),
      });
      const contractorRef = doc(db, 'contractorProfiles', reviewingRequest.contractorId);
      const contractorDoc = await getDoc(contractorRef);
      if (contractorDoc.exists()) {
        const d = contractorDoc.data() || {};
        const newCount = (d.reviewCount || 0) + 1;
        const newRating = (((d.rating || 0) * (d.reviewCount || 0)) + reviewRating) / newCount;
        await updateDoc(contractorRef, {
          rating: Math.round(newRating * 10) / 10,
          reviewCount: newCount,
        });
      }
      setSubmittedReviews(prev => ({ ...prev, [reviewingRequest.id]: true }));
      setShowReview(false);
    } catch (err) { console.error(err); }
    setSubmittingReview(false);
  };

  const getJobEarningsSummary = (request) => {
    const confs = jobConfirmations[request.id] || [];
    const verified = confs.filter(c => c.status === 'verified');
    const totalHours = verified.reduce((sum, c) => sum + (c.confirmedHours || 0), 0);
    return {
      totalHours,
      totalEarnings: Math.round(totalHours * (request.workerRate || 0)),
      days: verified,
    };
  };

  const filtered = requests.filter(r => {
    if (filter === 'pending') return r.status === 'pending';
    if (filter === 'accepted') return r.status === 'accepted' && !isJobComplete(r);
    if (filter === 'completed') return isJobComplete(r);
    if (filter === 'declined') return r.status === 'declined';
    if (filter === 'cancelled') return r.status === 'cancelled';
    return true;
  });

  const getTabCount = (tab) => {
    if (tab === 'all') return requests.length;
    if (tab === 'completed') return requests.filter(r => isJobComplete(r)).length;
    if (tab === 'accepted') return requests.filter(r => r.status === 'accepted' && !isJobComplete(r)).length;
    return requests.filter(r => r.status === tab).length;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours);
    return `${h % 12 || 12}:${minutes} ${h >= 12 ? 'PM' : 'AM'}`;
  };

  const ratingLabels = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Great', 5: 'Excellent!' };

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        <h1 style={{ ...styles.logo, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>TradePower</h1>
        <button style={styles.backButton} onClick={() => navigate('/dashboard')}>← Back</button>
      </div>

      <div style={styles.content}>
        <h2 style={styles.title}>Job Requests</h2>
        <p style={styles.subtitle}>View and respond to incoming job requests from contractors</p>

        {!stripeLoading && !stripeOnboardingDone && (
          <div style={styles.stripeBanner}>
            <div style={styles.stripeBannerLeft}>
              <p style={styles.stripeBannerTitle}>💳 Set Up Payouts to Accept Jobs</p>
              <p style={styles.stripeBannerText}>
                Connect your bank account to receive payment. Takes about 5 minutes.
                Required before accepting any job.
              </p>
            </div>
            <button style={styles.stripeBannerBtn} onClick={() => setShowStripeModal(true)}>
              Connect Bank →
            </button>
          </div>
        )}

        {!stripeLoading && stripeOnboardingDone && (
          <div style={styles.stripeConnectedBanner}>
            ✅ Bank account connected — you're ready to accept jobs and receive payouts every Thursday
          </div>
        )}

        <div style={styles.tabs}>
          {['pending', 'accepted', 'completed', 'declined', 'cancelled', 'all'].map(tab => (
            <button key={tab}
              style={filter === tab ? styles.tabActive : styles.tab}
              onClick={() => setFilter(tab)}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              <span style={styles.tabCount}>{getTabCount(tab)}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div style={styles.emptyState}><p>Loading requests...</p></div>
        ) : filtered.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyIcon}>📬</p>
            <p style={styles.emptyTitle}>No {filter} requests</p>
            <p style={styles.emptyText}>
              {filter === 'pending'
                ? 'When contractors send you job requests they will appear here'
                : `No ${filter} requests to show`}
            </p>
          </div>
        ) : (
          <div style={styles.list}>
            {filtered.map(request => {
              const late = isWithin48Hours(request.jobDate, request.jobTime);
              const complete = isJobComplete(request);
              const alreadyReviewed = submittedReviews[request.id];
              const earnings = complete ? getJobEarningsSummary(request) : null;

              return (
                <div key={request.id} style={complete ? styles.cardCompleted : styles.card}>
                  <div style={styles.cardHeader}>
                    <div>
                      <h3 style={styles.contractorName}>📋 Job Request from Contractor</h3>
                      <p style={styles.contractorEmail}>{request.contractorEmail}</p>
                    </div>
                    <span style={
                      complete ? styles.completedBadge
                      : request.status === 'pending' ? styles.pendingBadge
                      : request.status === 'accepted' ? styles.acceptedBadge
                      : request.status === 'declined' ? styles.declinedBadge
                      : styles.cancelledBadge
                    }>
                      {complete ? '🏁 Completed'
                        : request.status === 'pending' ? '⏳ Pending'
                        : request.status === 'accepted' ? '✅ Active'
                        : request.status === 'declined' ? '❌ Declined'
                        : request.isLateCancellation ? '⚠️ Late Cancel' : '🚫 Cancelled'}
                    </span>
                  </div>

                  {request.status === 'accepted' && late && !complete && (
                    <div style={styles.lateWarning}>
                      ⚠️ This job starts within 48 hours. Cancelling now will count as a late cancellation.
                    </div>
                  )}

                  <div style={styles.detailsGrid}>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>📅 Dates</span>
                      <span style={styles.detailValue}>
                        {formatDate(request.jobDate)}
                        {request.jobEndDate && request.jobEndDate !== request.jobDate
                          ? ` → ${formatDate(request.jobEndDate)}` : ''}
                      </span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>🕐 Start Time</span>
                      <span style={styles.detailValue}>{formatTime(request.jobTime)}</span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>⏱ Hours/Day</span>
                      <span style={styles.detailValue}>{request.jobDuration} hours</span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>📆 Working Days</span>
                      <span style={styles.detailValue}>
                        {request.jobDays} day{request.jobDays !== 1 ? 's' : ''}
                        {(request.includeSaturdays || request.includeSundays) && (
                          <span style={styles.weekendNote}>
                            {' '}(incl. {[request.includeSaturdays && 'Sat', request.includeSundays && 'Sun'].filter(Boolean).join(' & ')})
                          </span>
                        )}
                      </span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>📍 Job Site</span>
                      <span style={styles.detailValue}>{request.jobAddress}</span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>💰 Your Rate</span>
                      <span style={styles.detailValue}>${request.workerRate}/hr</span>
                    </div>
                  </div>

                  {request.jobDescription && (
                    <div style={styles.descriptionBox}>
                      <span style={styles.detailLabel}>📝 Job Description</span>
                      <p style={styles.description}>{request.jobDescription}</p>
                    </div>
                  )}

                  {!complete && (
                    <div style={styles.earningsBox}>
                      <span style={styles.earningsLabel}>Estimated Earnings</span>
                      <span style={styles.earningsAmount}>
                        ${Math.ceil(request.workerRate * request.jobDuration * request.jobDays)}
                      </span>
                      <span style={styles.earningsNote}>
                        ${request.workerRate}/hr × {request.jobDuration} hrs × {request.jobDays} days
                      </span>
                    </div>
                  )}

                  {complete && earnings && (
                    <div style={styles.completedEarningsBox}>
                      <div style={styles.completedEarningsHeader}>
                        <span style={styles.completedEarningsTitle}>💰 Final Earnings Summary</span>
                        <span style={styles.payoutBadge}>📅 Paid on next Thursday payout cycle</span>
                      </div>
                      {earnings.days.length > 0 ? (
                        <div style={styles.dailyBreakdown}>
                          {earnings.days
                            .sort((a, b) => a.date?.localeCompare(b.date))
                            .map(day => (
                              <div key={day.date} style={styles.dailyRow}>
                                <span style={styles.dailyDate}>
                                  {new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', {
                                    weekday: 'short', month: 'short', day: 'numeric',
                                  })}
                                </span>
                                <span style={styles.dailyHours}>{day.confirmedHours}h</span>
                                <span style={styles.dailyEarnings}>
                                  ${Math.round(day.confirmedHours * request.workerRate)}
                                </span>
                              </div>
                            ))}
                          <div style={styles.dailyTotalRow}>
                            <span style={styles.dailyTotalLabel}>Total</span>
                            <span style={styles.dailyTotalHours}>{earnings.totalHours}h</span>
                            <span style={styles.dailyTotalAmount}>${earnings.totalEarnings}</span>
                          </div>
                        </div>
                      ) : (
                        <div style={styles.noConfirmationsNote}>
                          <p style={styles.noConfirmationsText}>⏳ Confirmations still processing — check back shortly.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {complete && (
                    <div style={alreadyReviewed ? styles.reviewedBanner : styles.reviewBanner}>
                      {alreadyReviewed ? (
                        <span style={styles.reviewedText}>⭐ You've reviewed this contractor — thank you!</span>
                      ) : (
                        <>
                          <div>
                            <p style={styles.reviewBannerTitle}>🎉 Job complete!</p>
                            <p style={styles.reviewBannerText}>How was working with this contractor?</p>
                          </div>
                          <button style={styles.reviewBtn} onClick={() => handleOpenReview(request)}>
                            ⭐ Leave Review
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  <div style={styles.actionButtons}>
                    <button style={styles.profileButton} onClick={() => handleViewContractorProfile(request)}>
                      👤 View Contractor
                    </button>
                    <button style={styles.messageButton} onClick={() => navigate(`/chat/${request.id}`)}>
                      💬 Message
                    </button>
                  </div>

                  {request.status === 'pending' && (
                    <div style={styles.decisionButtons}>
                      <button style={styles.declineButton} onClick={() => handleDecline(request.id)}>
                        ❌ Decline
                      </button>
                      <button
                        style={stripeOnboardingDone ? styles.acceptButton : styles.acceptButtonStripe}
                        onClick={() => handleAccept(request.id)}>
                        {stripeOnboardingDone ? '✅ Accept Job' : '💳 Set Up Payouts to Accept'}
                      </button>
                    </div>
                  )}

                  {request.status === 'accepted' && !complete && (
                    <>
                      <div style={styles.acceptedBanner}>
                        ✅ You accepted this job! Show up at {formatTime(request.jobTime)} on {formatDate(request.jobDate)} at the address above.
                      </div>
                      <button
                        style={late ? styles.cancelBtnLate : styles.cancelBtnNormal}
                        onClick={() => handleCancel(request)}>
                        {late ? '⚠️ Cancel (Late — will affect profile)' : '🚫 Cancel Job'}
                      </button>
                    </>
                  )}

                  {request.status === 'declined' && (
                    <div style={styles.declinedBanner}>❌ You declined this request.</div>
                  )}

                  {request.status === 'cancelled' && (
                    <div style={styles.cancelledBanner}>
                      🚫 {request.isLateCancellation
                        ? '⚠️ Late cancellation recorded on your profile.'
                        : 'You cancelled this job.'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Stripe Onboarding Modal */}
      {showStripeModal && (
        <div style={styles.modalOverlay} onClick={() => !stripeRedirecting && setShowStripeModal(false)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>💳 Set Up Payouts</h3>
              {!stripeRedirecting && (
                <button style={styles.modalClose} onClick={() => setShowStripeModal(false)}>✕</button>
              )}
            </div>
            <div style={styles.modalBody}>
              <div style={styles.stripeModalIcon}>🏦</div>
              <h4 style={styles.stripeModalTitle}>Connect Your Bank Account</h4>
              <p style={styles.stripeModalText}>
                Before you can accept jobs, you need to connect your bank account so TradePower
                can send you payment every Thursday.
              </p>
              <div style={styles.stripeModalFeatures}>
                <div style={styles.stripeModalFeature}>
                  <span style={styles.stripeModalFeatureIcon}>💰</span>
                  <span style={styles.stripeModalFeatureText}>Paid every Thursday for the prior week's confirmed hours</span>
                </div>
                <div style={styles.stripeModalFeature}>
                  <span style={styles.stripeModalFeatureIcon}>📋</span>
                  <span style={styles.stripeModalFeatureText}>Stripe issues your 1099-NEC automatically if you earn over $600/year</span>
                </div>
                <div style={styles.stripeModalFeature}>
                  <span style={styles.stripeModalFeatureIcon}>🔒</span>
                  <span style={styles.stripeModalFeatureText}>Secured by Stripe — TradePower never sees your bank details</span>
                </div>
                <div style={styles.stripeModalFeature}>
                  <span style={styles.stripeModalFeatureIcon}>⏱️</span>
                  <span style={styles.stripeModalFeatureText}>Takes about 5 minutes — you'll return here automatically when done</span>
                </div>
              </div>
              {pendingAcceptId && (
                <div style={styles.stripeModalPending}>
                  ℹ️ Once your bank is connected, your job acceptance will process automatically.
                </div>
              )}
              <button
                style={stripeRedirecting ? styles.stripeModalBtnLoading : styles.stripeModalBtn}
                onClick={handleStartStripeOnboarding}
                disabled={stripeRedirecting}>
                {stripeRedirecting ? '⏳ Redirecting to Stripe...' : '🏦 Connect Bank Account →'}
              </button>
              {!stripeRedirecting && (
                <button style={styles.stripeModalSkip} onClick={() => setShowStripeModal(false)}>
                  Do this later
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReview && reviewingRequest && (
        <div style={styles.modalOverlay} onClick={() => setShowReview(false)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>⭐ Review Contractor</h3>
              <button style={styles.modalClose} onClick={() => setShowReview(false)}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <p style={styles.reviewSubtitle}>
                {reviewingRequest.jobDescription} • {formatDate(reviewingRequest.jobDate)}
                {reviewingRequest.jobEndDate !== reviewingRequest.jobDate
                  ? ` → ${formatDate(reviewingRequest.jobEndDate)}` : ''}
              </p>
              <p style={styles.reviewPrompt}>How would you rate this contractor?</p>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', margin: '12px 0' }}>
                {[1, 2, 3, 4, 5].map(star => (
                  <span key={star}
                    style={{
                      fontSize: '36px', cursor: 'pointer',
                      color: star <= (reviewHover || reviewRating) ? '#FFB800' : '#ddd',
                      transition: 'color 0.1s',
                    }}
                    onClick={() => setReviewRating(star)}
                    onMouseEnter={() => setReviewHover(star)}
                    onMouseLeave={() => setReviewHover(0)}>
                    ★
                  </span>
                ))}
              </div>
              {(reviewHover || reviewRating) > 0 && (
                <p style={styles.ratingLabel}>{ratingLabels[reviewHover || reviewRating]}</p>
              )}
              <label style={styles.fieldLabel}>Comments (optional)</label>
              <textarea
                style={styles.textarea}
                placeholder="Describe your experience — communication, payment, site conditions..."
                value={reviewComment}
                onChange={e => setReviewComment(e.target.value)}
                rows={4}
              />
              <div style={styles.modalButtons}>
                <button style={styles.cancelModalBtn} onClick={() => setShowReview(false)}>Cancel</button>
                <button
                  style={reviewRating > 0 && !submittingReview ? styles.submitReviewBtn : styles.submitReviewBtnDisabled}
                  onClick={handleSubmitReview}
                  disabled={!reviewRating || submittingReview}>
                  {submittingReview ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contractor Profile Modal */}
      {showProfile && viewedProfile && (
        <div style={styles.modalOverlay} onClick={() => setShowProfile(false)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>🏗️ Contractor Profile</h3>
              <button style={styles.modalClose} onClick={() => setShowProfile(false)}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.modalProfileHeader}>
                <div style={styles.modalAvatarPlaceholder}><span style={{ fontSize: '32px' }}>🏗️</span></div>
                <div>
                  <p style={styles.modalName}>{viewedProfile.companyName || 'Company'}</p>
                  <p style={styles.modalSub}>{viewedProfile.contactName || viewedProfile.email}</p>
                  {viewedProfile.phone && <p style={styles.modalSub}>{viewedProfile.phone}</p>}
                  {viewedProfile.rating > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginTop: '4px' }}>
                      {[1,2,3,4,5].map(s => (
                        <span key={s} style={{ color: s <= Math.round(viewedProfile.rating || 0) ? '#FFB800' : '#ddd', fontSize: '14px' }}>★</span>
                      ))}
                      <span style={{ fontSize: '12px', color: '#999', marginLeft: '4px' }}>
                        {viewedProfile.rating?.toFixed(1)} ({viewedProfile.reviewCount} reviews)
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {viewedProfile.state && (
                <div style={styles.modalSection}>
                  <p style={styles.modalSectionLabel}>State of Operation</p>
                  <p style={styles.modalSectionValue}>{viewedProfile.state}</p>
                </div>
              )}
              {viewedProfile.licenseNumber && (
                <div style={styles.modalSection}>
                  <p style={styles.modalSectionLabel}>Contractor License</p>
                  <p style={styles.modalSectionValue}>{viewedProfile.licenseState} #{viewedProfile.licenseNumber}</p>
                </div>
              )}
              {viewedProfile.trades?.length > 0 && (
                <div style={styles.modalSection}>
                  <p style={styles.modalSectionLabel}>Trades</p>
                  <div style={styles.modalTags}>
                    {viewedProfile.trades.map(t => <span key={t} style={styles.modalTag}>{t}</span>)}
                  </div>
                </div>
              )}
              <div style={styles.modalSection}>
                <p style={styles.modalSectionLabel}>Insurance</p>
                <span style={viewedProfile.insuranceAcknowledged ? styles.compBadge : styles.warnBadge}>
                  {viewedProfile.insuranceAcknowledged ? '✓ Covers 1099 Subs' : '⚠ Not Confirmed'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  navbar: {
    backgroundColor: 'white', padding: '16px 24px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  logo: { fontSize: '24px', fontWeight: 'bold', color: '#E94560', margin: 0 },
  backButton: {
    padding: '8px 16px', backgroundColor: 'transparent',
    color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer',
    touchAction: 'manipulation',
  },
  content: { maxWidth: '800px', margin: '0 auto', padding: '24px 16px' },
  title: { fontSize: '26px', color: '#1a1a2e', marginBottom: '8px' },
  subtitle: { color: '#666', marginBottom: '24px' },
  stripeBanner: {
    backgroundColor: '#fff8e1', border: '2px solid #FFB800',
    borderRadius: '12px', padding: '16px 20px', marginBottom: '24px',
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', gap: '16px', flexWrap: 'wrap',
  },
  stripeBannerLeft: { flex: 1 },
  stripeBannerTitle: { fontSize: '15px', fontWeight: 'bold', color: '#856404', marginBottom: '4px' },
  stripeBannerText: { fontSize: '13px', color: '#856404' },
  stripeBannerBtn: {
    padding: '10px 20px', backgroundColor: '#FFB800', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', fontWeight: 'bold', flexShrink: 0, touchAction: 'manipulation',
  },
  stripeConnectedBanner: {
    backgroundColor: '#d4edda', border: '1px solid #28a745',
    borderRadius: '10px', padding: '12px 16px', marginBottom: '24px',
    fontSize: '13px', color: '#155724', fontWeight: 'bold',
  },
  tabs: { display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' },
  tab: {
    padding: '8px 16px', backgroundColor: 'white', color: '#666',
    border: '1px solid #ddd', borderRadius: '20px', cursor: 'pointer',
    fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px',
    touchAction: 'manipulation',
  },
  tabActive: {
    padding: '8px 16px', backgroundColor: '#E94560', color: 'white',
    border: '1px solid #E94560', borderRadius: '20px', cursor: 'pointer',
    fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px',
    touchAction: 'manipulation',
  },
  tabCount: { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '10px', padding: '1px 6px', fontSize: '11px' },
  emptyState: { textAlign: 'center', padding: '60px', color: '#666' },
  emptyIcon: { fontSize: '48px', marginBottom: '12px' },
  emptyTitle: { fontSize: '20px', color: '#1a1a2e', marginBottom: '8px' },
  emptyText: { color: '#666', fontSize: '14px' },
  list: { display: 'flex', flexDirection: 'column', gap: '20px' },
  card: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '20px' },
  cardCompleted: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '20px', border: '2px solid #28a745' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' },
  contractorName: { fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  contractorEmail: { fontSize: '13px', color: '#999' },
  completedBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 },
  pendingBadge: { backgroundColor: '#fff3cd', color: '#856404', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 },
  acceptedBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 },
  declinedBadge: { backgroundColor: '#f8d7da', color: '#721c24', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 },
  cancelledBadge: { backgroundColor: '#f0f0f0', color: '#666', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 },
  lateWarning: { backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#856404', marginBottom: '16px', lineHeight: '1.5' },
  detailsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' },
  detailItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
  detailLabel: { fontSize: '11px', color: '#999', textTransform: 'uppercase', fontWeight: 'bold' },
  detailValue: { fontSize: '14px', color: '#333' },
  weekendNote: { fontSize: '12px', color: '#E94560' },
  descriptionBox: { marginBottom: '16px' },
  description: { fontSize: '14px', color: '#555', lineHeight: '1.6', marginTop: '6px' },
  earningsBox: { backgroundColor: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '8px', padding: '14px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '4px' },
  earningsLabel: { fontSize: '12px', color: '#276749', fontWeight: 'bold' },
  earningsAmount: { fontSize: '28px', fontWeight: 'bold', color: '#276749' },
  earningsNote: { fontSize: '12px', color: '#68d391' },
  completedEarningsBox: { backgroundColor: '#f0fff4', border: '2px solid #28a745', borderRadius: '10px', padding: '16px', marginBottom: '16px' },
  completedEarningsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' },
  completedEarningsTitle: { fontSize: '15px', fontWeight: 'bold', color: '#155724' },
  payoutBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold' },
  dailyBreakdown: { display: 'flex', flexDirection: 'column', gap: '6px' },
  dailyRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white', borderRadius: '6px', padding: '8px 12px' },
  dailyDate: { fontSize: '13px', color: '#333', flex: 1 },
  dailyHours: { fontSize: '13px', color: '#666', width: '40px', textAlign: 'center' },
  dailyEarnings: { fontSize: '14px', fontWeight: 'bold', color: '#28a745', width: '70px', textAlign: 'right' },
  dailyTotalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#d4edda', borderRadius: '6px', padding: '10px 12px', marginTop: '4px' },
  dailyTotalLabel: { fontSize: '13px', fontWeight: 'bold', color: '#155724', flex: 1 },
  dailyTotalHours: { fontSize: '13px', fontWeight: 'bold', color: '#155724', width: '40px', textAlign: 'center' },
  dailyTotalAmount: { fontSize: '16px', fontWeight: 'bold', color: '#155724', width: '70px', textAlign: 'right' },
  noConfirmationsNote: { backgroundColor: 'white', borderRadius: '8px', padding: '12px' },
  noConfirmationsText: { fontSize: '13px', color: '#856404', textAlign: 'center' },
  reviewBanner: { backgroundColor: '#fff8e1', border: '1px solid #FFB800', borderRadius: '8px', padding: '14px 16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  reviewedBanner: { backgroundColor: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' },
  reviewedText: { fontSize: '13px', color: '#276749', fontWeight: 'bold' },
  reviewBannerTitle: { fontSize: '14px', fontWeight: 'bold', color: '#856404', marginBottom: '2px' },
  reviewBannerText: { fontSize: '13px', color: '#856404' },
  reviewBtn: { padding: '8px 16px', backgroundColor: '#FFB800', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', flexShrink: 0, touchAction: 'manipulation' },
  actionButtons: { display: 'flex', gap: '10px', marginBottom: '12px' },
  profileButton: { flex: 1, padding: '10px', backgroundColor: 'transparent', color: '#1a1a2e', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  messageButton: { flex: 1, padding: '10px', backgroundColor: '#1a1a2e', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  decisionButtons: { display: 'flex', gap: '12px' },
  declineButton: { flex: 1, padding: '12px', backgroundColor: 'transparent', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  acceptButton: { flex: 2, padding: '12px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  acceptButtonStripe: { flex: 2, padding: '12px', backgroundColor: '#FFB800', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  acceptedBanner: { backgroundColor: '#d4edda', color: '#155724', padding: '12px 16px', borderRadius: '8px', fontSize: '14px', lineHeight: '1.5', marginBottom: '10px' },
  cancelBtnNormal: { width: '100%', padding: '10px', backgroundColor: 'transparent', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  cancelBtnLate: { width: '100%', padding: '10px', backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffc107', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  declinedBanner: { backgroundColor: '#f8d7da', color: '#721c24', padding: '12px 16px', borderRadius: '8px', fontSize: '14px' },
  cancelledBanner: { backgroundColor: '#f0f0f0', color: '#666', padding: '12px 16px', borderRadius: '8px', fontSize: '14px' },
  stripeModalIcon: { fontSize: '48px', textAlign: 'center', marginBottom: '12px' },
  stripeModalTitle: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e', textAlign: 'center', marginBottom: '10px' },
  stripeModalText: { fontSize: '14px', color: '#555', lineHeight: '1.6', textAlign: 'center', marginBottom: '20px' },
  stripeModalFeatures: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' },
  stripeModalFeature: { display: 'flex', alignItems: 'flex-start', gap: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '10px 12px' },
  stripeModalFeatureIcon: { fontSize: '18px', flexShrink: 0 },
  stripeModalFeatureText: { fontSize: '13px', color: '#333', lineHeight: '1.5' },
  stripeModalPending: { backgroundColor: '#e8f4fd', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#2c5282', marginBottom: '16px' },
  stripeModalBtn: { width: '100%', padding: '14px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '10px', touchAction: 'manipulation' },
  stripeModalBtnLoading: { width: '100%', padding: '14px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: 'not-allowed', marginBottom: '10px' },
  stripeModalSkip: { width: '100%', padding: '10px', backgroundColor: 'transparent', color: '#999', border: 'none', fontSize: '13px', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modalCard: { backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '560px', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 12px', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, backgroundColor: 'white' },
  modalTitle: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e' },
  modalClose: { width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#f0f0f0', border: 'none', cursor: 'pointer', fontSize: '14px', touchAction: 'manipulation' },
  modalBody: { padding: '20px 24px' },
  reviewSubtitle: { fontSize: '13px', color: '#666', marginBottom: '16px', textAlign: 'center' },
  reviewPrompt: { fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', textAlign: 'center', marginBottom: '4px' },
  ratingLabel: { textAlign: 'center', fontSize: '16px', fontWeight: 'bold', color: '#FFB800', marginBottom: '16px' },
  fieldLabel: { display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#555', marginBottom: '6px' },
  textarea: { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', marginBottom: '16px' },
  modalButtons: { display: 'flex', gap: '12px' },
  cancelModalBtn: { flex: 1, padding: '12px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  submitReviewBtn: { flex: 2, padding: '12px', backgroundColor: '#FFB800', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
  submitReviewBtnDisabled: { flex: 2, padding: '12px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'not-allowed' },
  modalProfileHeader: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' },
  modalAvatarPlaceholder: { width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  modalName: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  modalSub: { fontSize: '13px', color: '#666', marginBottom: '2px' },
  modalSection: { marginBottom: '16px' },
  modalSectionLabel: { fontSize: '11px', color: '#999', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '6px' },
  modalSectionValue: { fontSize: '14px', color: '#333' },
  modalTags: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  modalTag: { padding: '4px 10px', backgroundColor: '#f0f0f0', borderRadius: '12px', fontSize: '12px', color: '#444' },
  compBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '4px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold' },
  warnBadge: { backgroundColor: '#fff3cd', color: '#856404', padding: '4px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold' },
};

export default JobRequests;